/**
 * Context and Asset Groups API
 * Functions for managing contexts and asset groups
 */

import { supabase } from './supabase'

export interface Context {
  id: string
  key: string
  name: string
  created_at: string
}

export interface AssetGroup {
  id: string
  context_id: string
  category_id: string
  key: string
  name: string
  is_system: boolean
  show_in_menu: boolean
  created_at: string
}

export interface EnabledGroup {
  group_id: string
  unit_id: string
  active: boolean
  enabled_at: string
  group_key: string
  group_name: string
  context_id: string
}

/**
 * Get all available contexts
 */
export async function getContexts(): Promise<Context[]> {
  try {
    const { data, error } = await supabase
      .from('contexts')
      .select('*')
      .order('name')

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching contexts:', error)
    throw error
  }
}

/**
 * Get context by key
 */
export async function getContextByKey(key: string): Promise<Context | null> {
  try {
    const { data, error } = await supabase
      .from('contexts')
      .select('*')
      .eq('key', key)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching context:', error)
    return null
  }
}

/**
 * List groups by context key
 */
export async function listGroupsByContext(contextKey: string): Promise<AssetGroup[]> {
  try {
    const { data, error } = await supabase
      .rpc('list_groups_by_context', { p_context_key: contextKey })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching groups by context:', error)
    throw error
  }
}

/**
 * Get enabled groups for a unit
 */
export async function getEnabledGroups(unitId: string): Promise<EnabledGroup[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_enabled_groups', { p_unit_id: unitId })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching enabled groups:', error)
    throw error
  }
}

/**
 * Save enabled groups for a unit
 * Upserts provided group ids as active and disables any previously active groups not in the list
 */
export async function saveEnabledGroups(unitId: string, groupIds: string[]): Promise<void> {
  try {
    console.log('[saveEnabledGroups] Calling RPC with:', { unitId, groupIds })
    
    const { data, error } = await supabase
      .rpc('save_enabled_groups_v2', { 
        p_unit_id: unitId,
        p_group_ids: groupIds 
      })

    if (error) {
      // Try to extract a deterministic, readable message even when the error
      // object has non-enumerable properties (supabase-js sometimes returns
      // errors that stringify to `{}` in the browser).
      const extractMessage = (err: any): string => {
        if (!err) return 'unknown error'
        if (typeof err === 'string') return err
        if (err.message) return err.message
        if (err.details) return err.details
        if (err.hint) return err.hint
        try {
          const names = Object.getOwnPropertyNames(err || {})
          if (names.length) return names.map(n => `${n}:${String((err as any)[n])}`).join('; ')
        } catch (e) {
          // ignore
        }
        try { return String(err) } catch (e) { return 'saveEnabledGroups RPC failed' }
      }

      const normalized = extractMessage(error)
      console.error('[saveEnabledGroups] RPC raw error:', error)
      console.error('[saveEnabledGroups] RPC normalized message:', normalized)
      throw new Error(normalized || 'saveEnabledGroups RPC failed')
    }
    
    console.log('[saveEnabledGroups] Success:', data)
  } catch (error: any) {
    console.error('[saveEnabledGroups] Caught error:', error)
    console.error('[saveEnabledGroups] Error type:', typeof error)
    console.error('[saveEnabledGroups] Error constructor:', error?.constructor?.name)
    console.error('[saveEnabledGroups] Error message:', error?.message)
    console.error('[saveEnabledGroups] Error stack:', error?.stack)
    
    // Try to extract all properties
    if (error && typeof error === 'object') {
      console.error('[saveEnabledGroups] Error keys:', Object.keys(error))
      console.error('[saveEnabledGroups] Error values:', Object.values(error))
      for (const key in error) {
        console.error(`[saveEnabledGroups] error.${key}:`, error[key])
      }
    }
    throw error
  }
}

/**
 * Update unit context
 */
export async function updateUnitContext(unitId: string, contextKey: string): Promise<void> {
  try {
    const { error } = await supabase
      .rpc('update_unit_context', { 
        p_unit_id: unitId,
        p_context_key: contextKey 
      })

    if (error) throw error
  } catch (error) {
    // Log full error details for easier debugging
    try {
      console.error('Error updating unit context:', error, JSON.stringify(error))
    } catch (e) {
      console.error('Error updating unit context (stringify failed):', error)
    }

    // Normalize thrown error to include a useful message
    const message = (error && (error as any).message) ? (error as any).message : JSON.stringify(error)
    throw new Error(`updateUnitContext failed: ${message}`)
  }
}

/**
 * Get asset categories by a list of ids
 */
export async function getCategoriesByIds(ids: string[]): Promise<{ id: string; name: string }[]> {
  try {
    if (!ids || ids.length === 0) return []
    const { data, error } = await supabase
      .from('asset_categories')
      .select('id,name')
      .in('id', ids)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching categories by ids:', error)
    throw error
  }
}
